import { useState, useEffect, useMemo } from "react";
import { todayISO } from "@/lib/utils";
import { Link } from "react-router";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Badge from "@/components/ui/badge/Badge";
import { deriveFlockStage, resolveSpentAge } from "@/pages/Dashboard/flockStage";
import { useAuthStore } from "@/store/useAuthStore";
import { Modal } from "@/components/ui/modal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { flockService, shedService, feedFormulaService, flockFormulaService, flockTransferService, breedService } from "@/services/masterDataService";
import { handleApiError } from "@/services/api";
import type { Flock, Shed, FeedFormula, FlockFormula, Breed } from "@/types/models";
import { useIsOwner } from "@/hooks/useRole";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

// Keyed on the Flock["status"] union rather than a bare string, so the three
// the database actually permits are the three that can appear here. The map
// used to carry a fourth, "transferred", which `flocks_status_check` forbids —
// a colour for a state no row can hold.
const statusColors: Record<Flock["status"], "success" | "error" | "warning" | "light"> = {
  active: "success",
  culled: "error",
  sold: "light",
};

// Stage is the answer to "what should this flock be doing right now", which is
// what the list is scanned for. "spent" is the one that earns its colour: a
// layer past its economic life is a decision waiting to be made.
const stageColors: Record<string, "success" | "error" | "warning" | "light"> = {
  brooding: "light",
  growing: "light",
  laying: "success",
  spent: "warning",
};

export default function FlocksPage() {
  const isOwner = useIsOwner();
  // Matches the API: an owner OR a supervisor may move a flock (a manager may not).
  const canTransfer = useAuthStore((st) => st.user?.role === "owner" || st.user?.role === "supervisor");
  // Per-farm spent-layer age (weeks), resolved the same way the dashboard and
  // the backend's cull alert resolve it — three places showing a different
  // "spent" threshold is worse than none of them showing it.
  const spentAge = useAuthStore((s) =>
    resolveSpentAge(s.farm?.features?.spent_layer_age_weeks),
  );
  const [flocks, setFlocks] = useState<Flock[]>([]);
  const [sheds, setSheds] = useState<Shed[]>([]);
  const [formulas, setFormulas] = useState<FeedFormula[]>([]);
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Flock | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Formula history modal
  const [formulaModalOpen, setFormulaModalOpen] = useState(false);
  const [formulaFlock, setFormulaFlock] = useState<Flock | null>(null);
  const [formulaHistory, setFormulaHistory] = useState<FlockFormula[]>([]);
  const [formulaLoading, setFormulaLoading] = useState(false);
  const [assignFormulaId, setAssignFormulaId] = useState("");
  const [assignStartDate, setAssignStartDate] = useState("");
  const [assignNotes, setAssignNotes] = useState("");
  const [assignSaving, setAssignSaving] = useState(false);
  const [formulaError, setFormulaError] = useState("");

  // Transfer modal
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferFlockId, setTransferFlockId] = useState("");
  const [transferDate, setTransferDate] = useState("");
  const [transferNotes, setTransferNotes] = useState("");
  const [transferSaving, setTransferSaving] = useState(false);
  const [transferError, setTransferError] = useState("");

  // Filters
  const [filterStatus, setFilterStatus] = useState("");

  const displayRows = useMemo(() => {
    return flocks
      .filter((f) => {
        if (filterStatus && f.status !== filterStatus) return false;
        return true;
      })
      .map((f) => ({
        key: `flock-${f.id}`,
        name: f.batch_name,
        breed: f.breed_name || "-",
        shed_code: f.shed_code ?? "-",
        destination: f.destination_shed_code || undefined,
        formula: f.default_formula_name || "-",
        age_weeks: f.age_weeks,
        // Only for a LIVE flock. A culled 80-week layer would otherwise carry
        // a "spent" badge beside its "culled" status — a decision presented as
        // outstanding when it has already been made.
        stage: f.status === "active" ? deriveFlockStage(f.shed_type, f.age_weeks, spentAge) : null,
        initial_count: f.initial_count,
        current_count: f.current_count,
        // From the SERVER, not computed here. This used to be
        // current_count / initial_count — but current_count is decremented by
        // bird SALES as well as deaths, so a flock that sold 300 spent hens
        // read 65% on this list and 95% on its own detail page, under the
        // same word, one click apart. Birds sold are not birds lost.
        livability_pct: f.livability_pct ?? null,
        status: f.status,
        flock: f,
      }));
  }, [flocks, filterStatus, spentAge]);

  // Form state
  const [batchName, setBatchName] = useState("");
  const [shedId, setShedId] = useState("");
  const [arrivalDate, setArrivalDate] = useState("");
  const [initialCount, setInitialCount] = useState("");
  const [currentCount, setCurrentCount] = useState("");
  const [status, setStatus] = useState<"active" | "culled" | "sold">("active");
  const [notes, setNotes] = useState("");
  const [breedId, setBreedId] = useState("");
  const [shiftingDate, setShiftingDate] = useState("");
  const [eggStartDate, setEggStartDate] = useState("");
  const [onboarded, setOnboarded] = useState(false);
  const [destinationShedId, setDestinationShedId] = useState("");

  const loadData = async () => {
    setError("");
    try {
      const [flocksData, shedsData, formulaData, breedsData] = await Promise.all([
        flockService.list(),
        shedService.list(),
        feedFormulaService.list(),
        breedService.list(),
      ]);
      setFlocks(flocksData);
      setSheds(shedsData);
      setFormulas((formulaData as FeedFormula[]).filter((f) => f.active));
      setBreeds(breedsData);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setBatchName("");
    const firstShed = sheds.find((s) => s.status === "active");
    setShedId(firstShed ? firstShed.id.toString() : "");
    setArrivalDate(todayISO());
    setInitialCount("");
    setCurrentCount("");
    setStatus("active");
    setNotes("");
    setBreedId("");
    setShiftingDate("");
    setEggStartDate("");
    setOnboarded(false);
    setDestinationShedId("");
    setError("");
    setModalOpen(true);
  };

  const openEdit = (flock: Flock) => {
    setEditing(flock);
    setBatchName(flock.batch_name);
    setShedId(flock.shed_id.toString());
    setArrivalDate(flock.arrival_date.split("T")[0]);
    setInitialCount(flock.initial_count.toString());
    setCurrentCount(flock.current_count.toString());
    setStatus(flock.status as "active" | "culled" | "sold");
    setNotes(flock.notes ?? "");
    setBreedId(flock.breed_id ? flock.breed_id.toString() : "");
    setShiftingDate(flock.shifting_date ? flock.shifting_date.split("T")[0] : "");
    setEggStartDate(flock.egg_start_date ? flock.egg_start_date.split("T")[0] : "");
    setOnboarded(flock.onboarded ?? false);
    setDestinationShedId(flock.destination_shed_id ? flock.destination_shed_id.toString() : "");
    setError("");
    setModalOpen(true);
  };

  // Where the birds are decides whether they have a destination (and whether
  // they lay) — not a separate "type" the form used to ask for.
  const inBrooder = sheds.find((s) => s.id.toString() === shedId)?.shed_type === "brooder";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    const payload: Record<string, unknown> = {
      batch_name: batchName,
      // Every flock on a layer farm is layer birds; whether they lay depends
      // on the shed they are in, so the form no longer asks.
      flock_type: "layer",
      arrival_date: arrivalDate,
      status,
      notes: notes || undefined,
      breed_id: breedId ? parseInt(breedId) : undefined,
      shifting_date: shiftingDate || undefined,
      egg_start_date: eggStartDate || undefined,
      destination_shed_id: destinationShedId ? parseInt(destinationShedId) : undefined,
    };
    // Only the update handler accepts `onboarded`; the create request rejects
    // unknown fields. New flocks are never onboarded anyway. An edit sends
    // neither the counts nor the shed: counts move through logs, sales and
    // purchases, and the shed through a transfer.
    if (editing) {
      payload.onboarded = onboarded;
      // Status goes only when the owner changed it. Sales close and reopen a
      // flock on their own (active ↔ sold); sending the copy loaded with the
      // form would undo one that landed while it was open.
      if (status === editing.status) delete payload.status;
    } else {
      payload.shed_id = parseInt(shedId);
      payload.initial_count = parseInt(initialCount);
      payload.current_count = parseInt(currentCount);
    }

    try {
      if (editing) {
        await flockService.update(editing.id, payload);
      } else {
        await flockService.create(payload);
      }
      setModalOpen(false);
      await loadData();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // Flocks in brooder sheds available for transfer
  const broaderSheds = useMemo(() => new Set(sheds.filter(s => s.shed_type === "brooder").map(s => s.id)), [sheds]);
  const broodingFlocks = useMemo(
    () => flocks.filter((f) => f.status === "active" && f.destination_shed_id && broaderSheds.has(f.shed_id) && f.current_count > 0),
    [flocks, broaderSheds]
  );

  const openTransfer = () => {
    setTransferFlockId(broodingFlocks.length === 1 ? broodingFlocks[0].id.toString() : "");
    setTransferDate(todayISO());
    setTransferNotes("");
    setTransferError("");
    setTransferModalOpen(true);
  };

  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    setTransferSaving(true);
    setTransferError("");
    try {
      await flockTransferService.create({
        flock_id: parseInt(transferFlockId),
        transfer_date: transferDate,
        notes: transferNotes || undefined,
      });
      setTransferModalOpen(false);
      await loadData();
    } catch (err) {
      setTransferError(handleApiError(err));
    } finally {
      setTransferSaving(false);
    }
  };

  const openFormulaHistory = async (flock: Flock) => {
    setFormulaFlock(flock);
    setFormulaHistory([]);
    setFormulaError("");
    setAssignFormulaId("");
    setAssignStartDate(todayISO());
    setAssignNotes("");
    setFormulaModalOpen(true);
    setFormulaLoading(true);
    try {
      const history = await flockFormulaService.list(flock.id);
      setFormulaHistory(history ?? []);
    } catch (err) {
      setFormulaError(handleApiError(err));
    } finally {
      setFormulaLoading(false);
    }
  };

  const handleAssignFormula = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formulaFlock || !assignFormulaId) return;
    setAssignSaving(true);
    setFormulaError("");
    try {
      await flockFormulaService.assign(formulaFlock.id, {
        formula_id: parseInt(assignFormulaId),
        start_date: assignStartDate,
        notes: assignNotes || undefined,
      });
      // Reload history
      const history = await flockFormulaService.list(formulaFlock.id);
      setFormulaHistory(history ?? []);
      setAssignFormulaId("");
      setAssignNotes("");
      // Reload flocks to update current formula display
      await loadData();
    } catch (err) {
      setFormulaError(handleApiError(err));
    } finally {
      setAssignSaving(false);
    }
  };

  return (
    <>
      <PageMeta title="Flocks | Poultry Book" description="Manage farm flocks" />
      <PageBreadcrumb pageTitle="Flocks" />

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            All Flocks
          </h3>
          {canTransfer && (
            <div className="flex gap-2">
              {/* The API lets an owner OR a supervisor move a flock; the
                  supervisor is the one standing at the shed. */}
              {canTransfer && broodingFlocks.length > 0 && (
                <Button size="sm" variant="outline" onClick={openTransfer}>
                  Transfer
                </Button>
              )}
              {isOwner && (
                <Button size="sm" onClick={openCreate}>
                  Add Flock
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 sm:px-6">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={`${selectClass} max-w-[160px]`}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="culled">Culled</option>
            <option value="sold">Sold</option>
          </select>
        </div>

        {error && !modalOpen && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : displayRows.length === 0 && !error ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              {flocks.length === 0 ? "No flocks found. Add your first flock." : "No flocks match the selected filters."}
            </div>
          ) : displayRows.length === 0 ? null : (
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Flock
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Breed
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Shed
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Destination
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Formula
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Age
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Stage
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    Count
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    Livability
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Status
                  </TableCell>
                  {isOwner && (
                    <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayRows.map((row) => (
                  <TableRow
                    key={row.key}
                    className="border-t border-gray-100 dark:border-gray-800"
                  >
                    <TableCell className="px-5 py-3 text-sm font-medium text-gray-800 dark:text-white/90">
                      <Link
                        to={`/flocks/${row.flock?.id}`}
                        className="text-brand-500 hover:text-brand-600 dark:text-brand-400 hover:underline"
                      >
                        {row.name}
                      </Link>
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {row.breed}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {row.shed_code}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {row.destination ?? "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {row.flock ? (
                        <button
                          onClick={() => openFormulaHistory(row.flock!)}
                          className="text-brand-500 hover:text-brand-600 dark:text-brand-400 hover:underline"
                        >
                          {row.formula}
                        </button>
                      ) : (
                        row.formula
                      )}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {row.age_weeks}w
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {row.stage ? (
                        <Badge size="sm" color={stageColors[row.stage] ?? "light"}>
                          {row.stage}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right text-gray-600 dark:text-gray-400">
                      {row.current_count.toLocaleString()} / {row.initial_count.toLocaleString()}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right tabular-nums text-gray-600 dark:text-gray-400">
                      {row.livability_pct != null ? (
                        `${row.livability_pct.toFixed(1)}%`
                      ) : (
                        // An onboarded flock's initial_count predates the app,
                        // so deaths and initial sit on inconsistent bases and
                        // the ratio can read over 100%. Suppressed for exactly
                        // the reason flock economics suppresses mortality%.
                        <span
                          title={
                            row.flock.onboarded
                              ? "Not available for a flock onboarded mid-cycle — its starting count predates the app"
                              : "Not available — this flock has no starting count recorded"
                          }
                        >
                          —
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="px-5 py-3">
                      <Badge
                        size="sm"
                        color={statusColors[row.status] ?? /* a status the server added but this build has not */ "light"}
                      >
                        {row.status}
                      </Badge>
                    </TableCell>
                    {isOwner && (
                      <TableCell className="px-5 py-3 text-right">
                        {row.flock && (
                          <button
                            onClick={() => openEdit(row.flock!)}
                            className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                          >
                            Edit
                          </button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        className="max-w-md p-5 sm:p-8 mx-4"
      >
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editing ? "Edit Flock" : "Add Flock"}
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="batchName">Flock Name *</Label>
            <Input
              id="batchName"
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
              placeholder="e.g. Layer-1"
              required
              maxLength={100}
            />
          </div>

          <div>
            <Label htmlFor="shedId">{editing ? "Shed (move it by transfer)" : "Shed *"}</Label>
            <select
              id="shedId"
              value={shedId}
              onChange={(e) => {
                const val = e.target.value;
                setShedId(val);
              }}
              required
              disabled={!!editing}
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            >
              <option value="">Select shed</option>
              {sheds
                .filter((s) => s.status === "active")
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.shed_code} ({s.shed_type})
                  </option>
                ))}
            </select>
          </div>

          {inBrooder && (
            <div>
              <Label htmlFor="destinationShed">Destination Layer Shed</Label>
              <select
                id="destinationShed"
                value={destinationShedId}
                onChange={(e) => setDestinationShedId(e.target.value)}
                className={selectClass}
              >
                <option value="">Select destination</option>
                {sheds
                  .filter((s) => s.shed_type === "layer" && s.status === "active")
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.shed_code}
                    </option>
                  ))}
              </select>
            </div>
          )}

          <div>
            <Label htmlFor="arrivalDate">Arrival Date *</Label>
            <Input
              id="arrivalDate"
              type="date"
              value={arrivalDate}
              onChange={(e) => setArrivalDate(e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="breed">Breed</Label>
            <select
              id="breed"
              value={breedId}
              onChange={(e) => setBreedId(e.target.value)}
              className={selectClass}
            >
              <option value="">Select breed</option>
              {breeds.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          {editing ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="initialCount">Initial Count</Label>
                <Input
                  id="initialCount"
                  type="number"
                  value={initialCount}
                  disabled
                />
              </div>
              <div>
                <Label htmlFor="currentCount">Current Count</Label>
                <Input
                  id="currentCount"
                  type="number"
                  value={currentCount}
                  disabled
                />
              </div>
            </div>
          ) : (
            <div>
              <Label htmlFor="initialCount">Bird Count *</Label>
              <Input
                id="initialCount"
                type="number"
                value={initialCount}
                onChange={(e) => {
                  setInitialCount(e.target.value);
                  setCurrentCount(e.target.value);
                }}
                placeholder="e.g. 30000"
                required
                min={1}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="shiftingDate">Shifting to Layer Date</Label>
              <Input
                id="shiftingDate"
                type="date"
                value={shiftingDate}
                onChange={(e) => setShiftingDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="eggStartDate">Egg Start Date</Label>
              <Input
                id="eggStartDate"
                type="date"
                value={eggStartDate}
                onChange={(e) => setEggStartDate(e.target.value)}
              />
            </div>
          </div>

          {editing && (
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={onboarded}
                onChange={(e) => setOnboarded(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/20"
              />
              <span>
                Onboarded flock (entered mid-cycle at app adoption)
                <span className="mt-0.5 block text-xs text-gray-400 dark:text-gray-500">
                  Rearing cost predates the app &mdash; economics shows &ldquo;since adoption&rdquo; instead of a full-cycle P&amp;L.
                </span>
              </span>
            </label>
          )}

          {editing && (
            <div>
              <Label htmlFor="flockStatus">Status *</Label>
              <select
                id="flockStatus"
                value={status}
                onChange={(e) =>
                  setStatus(e.target.value as "active" | "culled" | "sold")
                }
                className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
              >
                <option value="active">Active</option>
                <option value="culled">Culled</option>
                <option value="sold">Sold</option>
              </select>
            </div>
          )}

          <div>
            <Label htmlFor="flockNotes">Notes</Label>
            <textarea
              id="flockNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              rows={2}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !batchName || !shedId}>
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Transfer Modal */}
      <Modal
        isOpen={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        className="max-w-md p-5 sm:p-8 mx-4"
      >
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          Transfer to Layer Shed
        </h4>

        {transferError && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {transferError}
          </div>
        )}

        <form onSubmit={handleTransfer} className="space-y-4">
          <div>
            <Label htmlFor="transferFlock">Brooding Flock *</Label>
            <select
              id="transferFlock"
              value={transferFlockId}
              onChange={(e) => setTransferFlockId(e.target.value)}
              required
              className={selectClass}
            >
              <option value="">Select flock</option>
              {broodingFlocks.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.batch_name} — {f.shed_code} → {f.destination_shed_code} ({f.current_count.toLocaleString()} birds)
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="transferDate">Transfer Date *</Label>
            <Input
              id="transferDate"
              type="date"
              value={transferDate}
              onChange={(e) => setTransferDate(e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="transferNotes">Notes</Label>
            <Input
              id="transferNotes"
              value={transferNotes}
              onChange={(e) => setTransferNotes(e.target.value)}
              placeholder="Optional"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" onClick={() => setTransferModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={transferSaving || !transferFlockId}
            >
              {transferSaving ? "Transferring..." : "Transfer"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Formula History Modal */}
      <Modal
        isOpen={formulaModalOpen}
        onClose={() => setFormulaModalOpen(false)}
        className="max-w-lg p-5 sm:p-8 mx-4"
      >
        <h4 className="mb-1 text-lg font-semibold text-gray-800 dark:text-white/90">
          Formula History
        </h4>
        {formulaFlock && (
          <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
            {formulaFlock.batch_name} &middot; {formulaFlock.shed_code}
          </p>
        )}

        {formulaError && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {formulaError}
          </div>
        )}

        {/* Assign new formula */}
        {isOwner && (
          <form onSubmit={handleAssignFormula} className="mb-6 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <p className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
              Assign New Formula
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="assignFormula">Formula *</Label>
                <select
                  id="assignFormula"
                  value={assignFormulaId}
                  onChange={(e) => setAssignFormulaId(e.target.value)}
                  required
                  className={selectClass}
                >
                  <option value="">Select</option>
                  {formulas.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="assignDate">Start Date *</Label>
                <Input
                  id="assignDate"
                  type="date"
                  value={assignStartDate}
                  onChange={(e) => setAssignStartDate(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="mt-3">
              <Label htmlFor="assignNotes">Notes</Label>
              <Input
                id="assignNotes"
                value={assignNotes}
                onChange={(e) => setAssignNotes(e.target.value)}
                placeholder="e.g. switched due to low HDP"
              />
            </div>
            <div className="mt-3 flex justify-end">
              <Button type="submit" size="sm" disabled={assignSaving || !assignFormulaId}>
                {assignSaving ? "Saving..." : "Assign"}
              </Button>
            </div>
          </form>
        )}

        {/* History list */}
        {formulaLoading ? (
          <div className="py-6 text-center text-gray-500 dark:text-gray-400">Loading...</div>
        ) : formulaHistory.length === 0 ? (
          <div className="py-6 text-center text-gray-500 dark:text-gray-400">
            No formula assigned yet.
          </div>
        ) : (
          <div className="space-y-3">
            {formulaHistory.map((ff) => (
              <div
                key={ff.id}
                className={`rounded-lg border p-3 ${
                  ff.is_current
                    ? "border-brand-200 bg-brand-50 dark:border-brand-800 dark:bg-brand-500/10"
                    : "border-gray-200 dark:border-gray-700"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {ff.formula_name}
                  </span>
                  {ff.is_current && (
                    <Badge size="sm" color="success">Current</Badge>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {new Date(ff.start_date).toLocaleDateString("en-IN")}
                  {" — "}
                  {ff.end_date
                    ? new Date(ff.end_date).toLocaleDateString("en-IN")
                    : "Present"}
                </p>
                {ff.notes && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
                    {ff.notes}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}
